import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Instagram,
  Facebook,
  Linkedin,
  Video,
  Heart,
  MessageCircle,
  Send,
  Bookmark,
  ThumbsUp,
  Share2,
  MoreHorizontal
} from 'lucide-react';
import { SocialAccount, SocialPlatform, PLATFORM_INFO } from '@/types/social';
import { cn } from '@/lib/core/utils';

interface PostPreviewProps {
  platform: SocialPlatform;
  content: string;
  mediaUrls?: string[];
  account?: SocialAccount;
}

export const PostPreview: React.FC<PostPreviewProps> = ({
  platform,
  content,
  mediaUrls = [],
  account
}) => {
  const info = PLATFORM_INFO[platform];
  const hasMedia = mediaUrls.length > 0;

  const renderInstagramPreview = () => (
    <div className="bg-white rounded-lg border overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-2">
          <Avatar className="h-8 w-8">
            <AvatarImage src={account?.account_avatar_url || undefined} />
            <AvatarFallback className="bg-gradient-to-br from-purple-500 to-pink-500 text-white text-xs">
              {account?.account_username?.[0]?.toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
          <span className="text-sm font-semibold">
            {account?.account_username || 'username'}
          </span>
        </div>
        <MoreHorizontal className="h-5 w-5 text-gray-600" />
      </div>

      {/* Media */}
      {hasMedia ? (
        <div className="aspect-square bg-gray-100 relative">
          <img
            src={mediaUrls[0]}
            alt="Post preview"
            className="w-full h-full object-cover"
          />
          {mediaUrls.length > 1 && (
            <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
              1/{mediaUrls.length}
            </div>
          )}
        </div>
      ) : (
        <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
          <Instagram className="h-12 w-12 text-gray-400" />
        </div>
      )}

      {/* Actions */}
      <div className="p-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Heart className="h-6 w-6" />
            <MessageCircle className="h-6 w-6" />
            <Send className="h-6 w-6" />
          </div>
          <Bookmark className="h-6 w-6" />
        </div>

        <p className="text-sm font-semibold">0 likes</p>

        <div className="text-sm">
          <span className="font-semibold">{account?.account_username || 'username'}</span>{' '}
          <span className="whitespace-pre-wrap">{content}</span>
        </div>
      </div>
    </div>
  );

  const renderFacebookPreview = () => (
    <div className="bg-white rounded-lg border overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 p-3">
        <Avatar className="h-10 w-10">
          <AvatarImage src={account?.account_avatar_url || undefined} />
          <AvatarFallback className="bg-blue-500 text-white">
            {account?.account_name?.[0]?.toUpperCase() || 'P'}
          </AvatarFallback>
        </Avatar>
        <div>
          <p className="text-sm font-semibold">
            {account?.account_name || 'Page Name'}
          </p>
          <p className="text-xs text-gray-500">Just now · 🌐</p>
        </div>
      </div>

      {/* Content */}
      <div className="px-3 pb-3">
        <p className="text-sm whitespace-pre-wrap">{content}</p>
      </div>

      {/* Media */}
      {hasMedia && (
        <div className="aspect-video bg-gray-100 relative">
          <img
            src={mediaUrls[0]}
            alt="Post preview"
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Reactions */}
      <div className="p-3 border-t">
        <div className="flex items-center justify-between text-gray-500 text-sm">
          <div className="flex items-center gap-1">
            <div className="flex -space-x-1">
              <span className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                <ThumbsUp className="h-3 w-3 text-white" />
              </span>
            </div>
            <span>0</span>
          </div>
          <span>0 comments</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex border-t">
        <button className="flex-1 flex items-center justify-center gap-2 py-2 hover:bg-gray-50">
          <ThumbsUp className="h-5 w-5 text-gray-500" />
          <span className="text-sm text-gray-500">Like</span>
        </button>
        <button className="flex-1 flex items-center justify-center gap-2 py-2 hover:bg-gray-50">
          <MessageCircle className="h-5 w-5 text-gray-500" />
          <span className="text-sm text-gray-500">Comment</span>
        </button>
        <button className="flex-1 flex items-center justify-center gap-2 py-2 hover:bg-gray-50">
          <Share2 className="h-5 w-5 text-gray-500" />
          <span className="text-sm text-gray-500">Share</span>
        </button>
      </div>
    </div>
  );

  const renderLinkedInPreview = () => (
    <div className="bg-white rounded-lg border overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 p-3">
        <Avatar className="h-12 w-12">
          <AvatarImage src={account?.account_avatar_url || undefined} />
          <AvatarFallback className="bg-blue-700 text-white">
            {account?.account_name?.[0]?.toUpperCase() || 'C'}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <p className="text-sm font-semibold">
            {account?.account_name || 'Company Name'}
          </p>
          <p className="text-xs text-gray-500">0 followers</p>
          <p className="text-xs text-gray-500">Just now · 🌐</p>
        </div>
        <MoreHorizontal className="h-5 w-5 text-gray-500" />
      </div>

      {/* Content */}
      <div className="px-3 pb-3">
        <p className="text-sm whitespace-pre-wrap">{content}</p>
      </div>

      {/* Media */}
      {hasMedia && (
        <div className="aspect-video bg-gray-100 relative">
          <img
            src={mediaUrls[0]}
            alt="Post preview"
            className="w-full h-full object-cover"
          />
        </div>
      )}

      {/* Reactions */}
      <div className="px-3 py-2 border-t text-xs text-gray-500">
        0 reactions
      </div>

      {/* Actions */}
      <div className="flex border-t px-2">
        <button className="flex-1 flex items-center justify-center gap-1 py-3 hover:bg-gray-50 rounded">
          <ThumbsUp className="h-4 w-4 text-gray-500" />
          <span className="text-xs text-gray-500">Like</span>
        </button>
        <button className="flex-1 flex items-center justify-center gap-1 py-3 hover:bg-gray-50 rounded">
          <MessageCircle className="h-4 w-4 text-gray-500" />
          <span className="text-xs text-gray-500">Comment</span>
        </button>
        <button className="flex-1 flex items-center justify-center gap-1 py-3 hover:bg-gray-50 rounded">
          <Share2 className="h-4 w-4 text-gray-500" />
          <span className="text-xs text-gray-500">Repost</span>
        </button>
        <button className="flex-1 flex items-center justify-center gap-1 py-3 hover:bg-gray-50 rounded">
          <Send className="h-4 w-4 text-gray-500" />
          <span className="text-xs text-gray-500">Send</span>
        </button>
      </div>
    </div>
  );

  const renderTikTokPreview = () => (
    <div className="bg-black rounded-lg overflow-hidden text-white">
      {/* Video Area */}
      <div className="aspect-[9/16] bg-gray-900 relative flex items-center justify-center">
        {hasMedia ? (
          <img
            src={mediaUrls[0]}
            alt="Post preview"
            className="w-full h-full object-cover"
          />
        ) : (
          <Video className="h-16 w-16 text-gray-600" />
        )}

        {/* Overlay Content */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <p className="font-semibold text-sm">
                @{account?.account_username || 'username'}
              </p>
              <p className="text-sm mt-1 line-clamp-3">{content}</p>
            </div>

            {/* Side Actions */}
            <div className="flex flex-col items-center gap-4">
              <Avatar className="h-10 w-10 border-2 border-white">
                <AvatarImage src={account?.account_avatar_url || undefined} />
                <AvatarFallback className="bg-gray-700">U</AvatarFallback>
              </Avatar>
              <div className="flex flex-col items-center">
                <Heart className="h-7 w-7" />
                <span className="text-xs">0</span>
              </div>
              <div className="flex flex-col items-center">
                <MessageCircle className="h-7 w-7" />
                <span className="text-xs">0</span>
              </div>
              <div className="flex flex-col items-center">
                <Share2 className="h-7 w-7" />
                <span className="text-xs">0</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  switch (platform) {
    case 'instagram':
      return renderInstagramPreview();
    case 'facebook':
      return renderFacebookPreview();
    case 'linkedin':
      return renderLinkedInPreview();
    case 'tiktok':
      return renderTikTokPreview();
    default:
      return null;
  }
};

export default PostPreview;
